import { useLanguage } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import { Mail, MessageCircle } from "lucide-react";
import { L, t } from "../lib/translations";

function TelegramIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
    </svg>
  );
}

function InstagramIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
    </svg>
  );
}

const CONTACTS = [
  {
    id: "telegram",
    icon: TelegramIcon,
    name: "Telegram",
    handle: "@iFollowers_israel",
    desc: { en: "Join our channel for updates & deals", ru: "Канал для обновлений и акций", he: "ערוץ לעדכונים ומבצעים" },
    url: "https://t.me/iFollowers_israel",
    accent: "#29b6f6",
    tag: { en: "Channel", ru: "Канал", he: "ערוץ" },
  },
  {
    id: "email",
    icon: Mail,
    name: "Email",
    handle: "ifollowers.israel@gmail.com",
    desc: { en: "Write us anytime — we reply within minutes", ru: "Пишите — отвечаем в течение минут", he: "כתבו בכל עת — עונים תוך דקות" },
    url: "mailto:ifollowers.israel@gmail.com",
    accent: "#E1306C",
    tag: { en: "Email", ru: "Почта", he: "מייל" },
  },
  {
    id: "instagram",
    icon: InstagramIcon,
    name: "Instagram",
    handle: "@ifollowers_israel",
    desc: { en: "Follow us for tips & updates", ru: "Следите за нами", he: "עקבו אחרינו לטיפים ועדכונים" },
    url: "https://www.instagram.com/ifollowers_israel/",
    accent: "#E1306C",
    tag: { en: "Instagram", ru: "Instagram", he: "אינסטגרם" },
  },
];

export default function Contact() {
  const { lang } = useLanguage();
  const { user } = useAuth();

  return (
    <div className="min-h-screen pt-14 pb-20">
      <div className="max-w-lg mx-auto px-4 py-8">

        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-black font-outfit" style={{ color: "var(--text-primary)" }}>
            {t("Contact.contact_us", lang)}
          </h1>
          <p className="text-sm mt-0.5" style={{ color: "var(--text-secondary)" }}>
            {t("Contact.choose_the_channel_that_works_best_for_you", lang)}
          </p>
        </div>

        {/* Response time */}
        <div className="flex items-center gap-2 mb-5 px-4 py-2.5 rounded-xl"
          style={{ background: "var(--surface-1)", border: "1px solid var(--border-color)" }}>
          <div className="w-2 h-2 rounded-full bg-green-400 flex-shrink-0 animate-pulse"
            style={{ boxShadow: "0 0 6px rgba(34,197,94,0.5)" }} />
          <p className="text-xs" style={{ color: "var(--text-secondary)" }}>
            {t("Contact.average_response_time_a_few_minutes", lang)}
          </p>
        </div>

        {/* Contact cards */}
        <div className="space-y-2.5 mb-2.5">
          {CONTACTS.map(c => {
            const IconCmp = c.icon;
            return (
              <a key={c.id} href={c.url} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-4 px-4 py-4 rounded-2xl border transition-all hover:opacity-80"
                style={{ borderColor: "var(--border-color)", background: "var(--surface-1)" }}>
                <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: `${c.accent}14`, border: `1px solid ${c.accent}28` }}>
                  <IconCmp className="w-5 h-5" style={{ color: c.accent }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="font-bold text-sm" style={{ color: "var(--text-primary)" }}>{c.name}</span>
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full"
                      style={{ background: `${c.accent}12`, color: c.accent, border: `1px solid ${c.accent}25` }}>
                      {c.tag[lang] || c.tag.en}
                    </span>
                  </div>
                  <div className="text-xs font-medium truncate" style={{ color: "var(--text-primary)" }}>{c.handle}</div>
                  <div className="text-[11px] mt-0.5" style={{ color: "var(--text-secondary)" }}>
                    {c.desc[lang] || c.desc.en}
                  </div>
                </div>
                <svg className="w-4 h-4 flex-shrink-0 rtl-flip" style={{ color: "var(--text-secondary)" }}
                  fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </a>
            );
          })}
        </div>

        {/* Live support — only for logged-in users */}
        {user && <button onClick={() => window.dispatchEvent(new CustomEvent("open-support"))}
          className="w-full flex items-center gap-4 px-4 py-4 rounded-2xl border transition-all hover:opacity-80 text-start"
          style={{ borderColor: "rgba(139,92,246,0.25)", background: "rgba(139,92,246,0.05)" }}>
          <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 relative"
            style={{ background: "rgba(139,92,246,0.12)", border: "1px solid rgba(139,92,246,0.25)" }}>
            <MessageCircle className="w-5 h-5" style={{ color: "#8B5CF6" }} />
            <div className="absolute -top-0.5 -end-0.5 w-3 h-3 rounded-full bg-green-400 border-2"
              style={{ borderColor: "var(--bg-primary)" }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-0.5">
              <span className="font-bold text-sm" style={{ color: "var(--text-primary)" }}>
                {t("Contact.live_support", lang)}
              </span>
              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full"
                style={{ background: "rgba(139,92,246,0.1)", color: "#8B5CF6", border: "1px solid rgba(139,92,246,0.2)" }}>
                24/7
              </span>
            </div>
            <div className="text-[11px]" style={{ color: "var(--text-secondary)" }}>
              {t("Contact.open_a_ticket_we_respond_within_minutes", lang)}
            </div>
          </div>
          <svg className="w-4 h-4 flex-shrink-0 rtl-flip" style={{ color: "var(--text-secondary)" }}
            fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>}

      </div>
    </div>
  );
}
